import Alert from '@pelckmans/elementis/components/alert';
import Button from '@pelckmans/elementis/components/button';
import Page from '@pelckmans/elementis/components/page';
import Tabs from '@pelckmans/elementis/components/tabs';
import Plus from '@pelckmans/elementis/icons/plus';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IdentityContext from '../../contexts/IdentityContext';
import ModalContext from '../../contexts/ModalContext';
import Role from '../../core/security/role';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import useThrowAsyncError from '../../hooks/useThrowAsyncError';
import PageLayout from '../../layout/Page.ianua';
import { fetchUserModules, fetchVisitedModules, UserModule } from './api/methodsApi';
import AddLicenseModal from '../../shared-components/AddLicenseModal';
import Catalogue from './components/Catalogue';
import EmptyState from './components/EmptyState';
import Method from './components/Method';
import UserRoleAlert from './components/UserRoleAlert';
import { sortModules } from './utils';
import ContentHeader from '../../layout/content/ContentHeader';
import ContentBody from '../../layout/content/ContentBody';
import {
  withScrollThreshold,
  WithScrollThresholdProps,
} from '../../shared-components/elementis/hoc/withScrollThreshold';

function MethodList({
  groupedModules,
  phasedOutModules,
  visitedModules,
  addedModuleIds,
}: {
  groupedModules: { years: number[]; modules: UserModule[] }[];
  phasedOutModules: { years: number[]; modules: UserModule[] }[];
  visitedModules: string[];
  addedModuleIds: string[];
}) {
  const { t } = useTranslation();
  return (
    <>
      <section
        className="active-modules modules-container"
        aria-label={t('PAGES.MY_METHODS.REGION.MODULES.A11Y_LABEL')}
      >
        {groupedModules?.map(({ years, modules }) =>
          modules.map((module, index: number) => {
            return (
              <Method
                key={module.id}
                module={module}
                position={index}
                visitedModules={visitedModules}
                years={years}
                isNew={addedModuleIds.includes(module.id)}
                scrollTo={addedModuleIds[0] === module.id}
              />
            );
          }),
        )}
      </section>
      {phasedOutModules.length > 0 && (
        <section className="phased-out-modules" aria-labelledby="phased-out-heading">
          <div className="block-header">
            <h2 className="block-header__title" id="phased-out-heading">
              {t('PAGES.MY_METHODS.REGION.PHASED_OUT_MODULES.TITLE')}
            </h2>
            <Alert prominence="warning">{t('PAGES.MY_METHODS.REGION.PHASED_OUT_MODULES.ALERT.MESSAGE')}</Alert>
          </div>
          <div className="modules-container">
            {phasedOutModules?.map(({ years, modules }) =>
              modules.map((module, index) => (
                <Method
                  key={module.id}
                  module={module}
                  position={index}
                  visitedModules={visitedModules}
                  years={years}
                  isNew={addedModuleIds.includes(module.id)}
                  scrollTo={addedModuleIds[0] === module.id}
                />
              )),
            )}
          </div>
        </section>
      )}
    </>
  );
}

interface MyMethodsProps extends WithScrollThresholdProps {}

type TabType = 'methods' | 'catalogue';

function MyMethods({ pastThreshold }: MyMethodsProps) {
  const { REACT_APP_P_STUDIO_GROUP } = process.env;
  const {
    identity: { isTeacherAlike, highestRole },
  } = useContext(IdentityContext);
  const isVoStudent = REACT_APP_P_STUDIO_GROUP === 'VO' && highestRole === Role.STUDENT;

  const [isLoading, setIsLoading] = useState(true);
  const [groupedModules, setGroupedModules] = useState<{ years: number[]; modules: UserModule[] }[]>([]);
  const [phasedOutModules, setPhasedOutModules] = useState<{ years: number[]; modules: UserModule[] }[]>([]);
  const [visitedModules, setVisitedModuleIds] = useState<string[]>([]);
  const [selectedTab, setSelectedTab] = useState<TabType>('methods');
  const [demoModules, setDemoModules] = useState<string[]>([]);
  const [userModules, setUserModules] = useState<UserModule[]>([]);
  const [addedModuleIds, setAddedModuleIds] = useState<string[]>([]);

  const { t } = useTranslation();
  const handleError = useThrowAsyncError();
  const { showModal } = useContext(ModalContext);

  useDocumentTitle(t('PAGES.MY_METHODS.DOCUMENT_TITLE'));

  const fetchModules = useCallback(async () => {
    const fetchedUserModules = await fetchUserModules(REACT_APP_P_STUDIO_GROUP);
    setUserModules(fetchedUserModules);

    try {
      if (highestRole === Role.STUDENT) {
        setGroupedModules(sortModules(fetchedUserModules, false));
        setIsLoading(false);
        return fetchedUserModules;
      }

      const demoModules = fetchedUserModules.filter(module => module.isDemo);
      setDemoModules(demoModules.map(module => module.id));

      const { phasedOut, others } = fetchedUserModules.reduce<{
        phasedOut: UserModule[];
        others: UserModule[];
      }>(
        (acc, item) => {
          (item.publishingState === 'phasedOut' ? acc.phasedOut : acc.others).push(item);
          return acc;
        },
        { phasedOut: [], others: [] },
      );

      setGroupedModules(sortModules(others, isTeacherAlike));
      setPhasedOutModules(sortModules(phasedOut, isTeacherAlike));

      return fetchedUserModules;
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  }, [REACT_APP_P_STUDIO_GROUP, highestRole, isTeacherAlike, handleError]);

  const refetchAfterModuleAdded = async () => {
    const oldModulesMap = new Map(userModules.map(m => [m.id, m]));

    const newModules = await fetchModules();
    const newIds: string[] = [];

    if (!newModules) return;

    for (const m of newModules) {
      const oldModule = oldModulesMap.get(m.id);
      if (!oldModule) {
        newIds.push(m.id);
      } else if (m.subModules.length > oldModule.subModules.length) {
        newIds.push(m.id);
      }
    }

    setAddedModuleIds(newIds);
  };

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  useEffect(() => {
    async function fetchVisited() {
      const modules = await fetchVisitedModules();

      setVisitedModuleIds(modules);
    }
    fetchVisited().catch(handleError);
  }, [handleError]);

  const showAddLicenseModal = () =>
    showModal(<AddLicenseModal id="add-license-modal" onRefetch={refetchAfterModuleAdded} />);

  if (isVoStudent) {
    return (
      <PageLayout>
        <Page>
          <ContentHeader
            cover={
              <Page.Header
                headerText={t('PAGES.MY_METHODS.PAGE_TITLE')}
                density={pastThreshold ? 'compact' : 'normal'}
              />
            }
          />
          <ContentBody>
            {groupedModules && (
              <MethodList
                phasedOutModules={phasedOutModules}
                groupedModules={groupedModules}
                visitedModules={visitedModules}
                addedModuleIds={addedModuleIds}
              />
            )}
          </ContentBody>
        </Page>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Page>
        <Tabs selectedTab={selectedTab} onTabChange={tab => setSelectedTab(tab as TabType)}>
          <ContentHeader
            cover={
              <Page.Header
                density={pastThreshold ? 'compact' : 'normal'}
                headerText={t('PAGES.MY_METHODS.HEADER.TITLE')}
                extra={
                  <Tabs.TabList
                    aria-label={t('PAGES.MY_METHODS.HEADER.TAB_LIST.A11Y_LABEL')}
                    size={pastThreshold ? 'sm' : 'md'}
                  >
                    <Tabs.Tab id="methods">{t('PAGES.MY_METHODS.HEADER.TAB_LIST.TABS.MY_METHODS')}</Tabs.Tab>
                    <Tabs.Tab id="catalogue">{t('PAGES.MY_METHODS.HEADER.TAB_LIST.TABS.CATALOGUE')}</Tabs.Tab>
                  </Tabs.TabList>
                }
                buttonGroupProps={
                  selectedTab === 'methods'
                    ? {
                        'aria-label': t('PAGES.MY_METHODS.HEADER.BUTTONS_GROUP.A11Y_LABEL'),
                        role: 'group',
                        children: (
                          <Button startIcon={<Plus />} prominence="secondary" onClick={showAddLicenseModal}>
                            {t('PAGES.MY_METHODS.HEADER.BUTTONS_GROUP.ACTIVATE_LICENSE')}
                          </Button>
                        ),
                      }
                    : undefined
                }
              />
            }
          />
          <ContentBody alerts={<UserRoleAlert />}>
            <Tabs.TabsContent>
              <Tabs.TabPanel id="methods">
                {groupedModules.length === 0 && phasedOutModules.length === 0 ? (
                  <EmptyState
                    isLoading={isLoading}
                    onRefetch={fetchModules}
                    aria-label={t('PAGES.MY_METHODS.LOADING_STATES.METHOD')}
                  />
                ) : (
                  <MethodList
                    phasedOutModules={phasedOutModules}
                    groupedModules={groupedModules}
                    visitedModules={visitedModules}
                    addedModuleIds={addedModuleIds}
                  />
                )}
              </Tabs.TabPanel>
              <Tabs.TabPanel id="catalogue">
                <Catalogue demoModules={demoModules} />
              </Tabs.TabPanel>
            </Tabs.TabsContent>
          </ContentBody>
        </Tabs>
      </Page>
    </PageLayout>
  );
}

export default withScrollThreshold(MyMethods);
